// /admin → Restaurants: the moderation and authoring side of Restaurant Changes.
//
// Its own file for the reason ChangelogAdmin.js and EventsAdmin.js are:
// AdminPage.js is a dashboard of tables, and this is three EDITORS — a queue
// with a decision on every row, an editorial list, and a per-restaurant form.
// None of that is what the rest of that file does.
//
// ## ⚠️ Nothing here edits a menu
//
// The authored menus live in `public/Map Info/pathfinder/places.json`, which
// `version.json` hashes — one byte changed there costs every phone a 13 MB
// refetch. This screen only ever writes the KV overlay through
// /api/restaurants, and it reads places.json purely to turn the ids on a review
// ("place_ms882w45_9 / mi_btc_01") into the names a moderator can act on
// ("Bell Tower Cafe · 1 Egg"). A queue of opaque ids is a queue nobody works.
//
// ## The five panels
//
//  1. Review queue — pending first, because that is the only one that is work.
//     Every card can also be DELETED (gone from every list and the average,
//     its photos deleted from Blob), and Denied has "Delete all" to clear it.
//  2. Dish photos — students' pictures of menu items (the `itemId` rows of
//     /api/place-photo): the waiting ones to accept or reject, and the live
//     ones to delete. They used to sit in Place photos beside study spots.
//  3. Weekly Highlights — up to five picks, ordered by hand.
//  4. Temporary menu items — a restaurant, then its specials and when they run.
//  5. Reviewer handles — the support tool, below.
//
// ## ⚠️ Handles: this screen is the ONLY place one can be changed
//
// The app lets a reviewer claim an `@handle` once and never offers a rename, so
// every "I typed it wrong", "that's my real name" and "somebody is pretending
// to be me" arrives as a message to support and is answered here. That makes
// this panel the whole recovery path for the feature rather than a convenience,
// which is why it can act on an author key that has no review in any queue: the
// person writing in may have had everything they wrote denied.
//
// A handle is stored against the author key and resolved when a review is READ,
// so a change made here is on every one of that person's past reviews the
// moment it saves — there is nothing to re-run and no row that can be missed.
//
// The schedule is `Promotion`'s, field for field, and `scheduleLine` below
// mirrors `promotionWhen` in UMapsMobile's src/restaurants/lib/menu.js so the
// sentence an operator reads while authoring is the sentence the app prints.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fmtTime } from '../utils/adminLabels.js';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

const PANELS = [
  { id: 'queue', label: 'Review queue' },
  { id: 'photos', label: 'Dish photos' },
  { id: 'picks', label: 'Weekly Highlights' },
  { id: 'temp', label: 'Temporary menu items' },
  { id: 'handles', label: 'Reviewer handles' },
];

const STATUS_FILTERS = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'denied', label: 'Denied' },
  { id: 'all', label: 'All' },
];

const MAX_PICKS = 5;

/**
 * ⚠️ The SERVER's caps, restated so the box refuses before the save does.
 *
 * `MAX_PICK_TITLE` / `MAX_PICK_SUBTITLE` in `api/_restaurantStore.js`, which is
 * where they are enforced — this copy can only ever refuse EARLIER and must
 * never be looser. The duplication is safe in exactly one direction, the same
 * way the handle length rules are duplicated for the claim box: a local copy
 * may refuse something the server would accept, and may never accept something
 * the server would refuse. They are short because of where the phone draws them:
 * over a slanted banner across the corner of the hero photo, the title at 17pt
 * over at most two lines and the subtitle at 13pt over one, inside about three
 * quarters of a card's width. A box that accepted more would be letting
 * somebody type words no reader will ever see.
 */
const MAX_PICK_TITLE = 48;
const MAX_PICK_SUBTITLE = 32;

// The order UMapsTools authors in, and the order promotionWhen reads in.
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

// A stored photo → a loadable URL. A Blob upload is absolute and passes
// through; a dev-machine upload is a path inside the served image library.
// Same rule AdminPage's own photoUrl follows, for the same two upload shapes.
const photoUrl = (u) => {
  const s = String(u || '');
  return /^(https?:|data:|blob:)/i.test(s) ? s : encodeURI(`/Map Info/UofMPics/${s}`);
};

const stars = (n) => '★'.repeat(Math.max(0, Math.min(5, n))) + '☆'.repeat(Math.max(0, 5 - n));

/** "14:00" → "2:00 PM". The `to12h` promotionWhen is handed. */
function to12h(t) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
  if (!m) return '';
  const h = Number(m[1]);
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-03-14" → "Mar 14". Parsed by hand: `new Date('2026-03-14')` is UTC. */
function shortDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : '';
}

/**
 * When a temporary item runs, in one line.
 *
 * ⚠️ The first two segments are `promotionWhen`'s output verbatim — same day
 * runs, same separator, same "all day" / "from" / "until" wording — so the
 * operator authoring it and the student reading it in the app are looking at
 * one sentence. The third segment is the date range, which promotionWhen does
 * not print (a promotion is drawn in context; here the operator has to be able
 * to see that the special they are typing expires on Saturday).
 */
export function scheduleLine(item) {
  const days = item?.days || {};
  const on = DAY_KEYS.filter((k) => days[k]);
  let dayPart = 'Every day';
  if (on.length && on.length < 7) {
    const runs = [];
    for (const k of DAY_KEYS) {
      if (!days[k]) { runs.push(null); continue; }
      const last = runs[runs.length - 1];
      if (last) last.end = k; else runs.push({ start: k, end: k });
    }
    dayPart = runs.filter(Boolean)
      .map((r) => (r.start === r.end ? DAY_SHORT[r.start] : `${DAY_SHORT[r.start]}–${DAY_SHORT[r.end]}`))
      .join(', ');
  }
  const s = item?.start ? to12h(item.start) : '';
  const e = item?.end ? to12h(item.end) : '';
  const timePart = s && e ? `${s} – ${e}` : s ? `from ${s}` : e ? `until ${e}` : 'all day';

  const from = shortDate(item?.startDate);
  const to = shortDate(item?.endDate);
  const datePart = from && to ? `${from} – ${to}` : from ? `from ${from}` : to ? `until ${to}` : '';

  // ⚠️ A window whose end is at or before its start runs PAST MIDNIGHT
  // (promotionActiveAt), which is legal and easy to read as a typo — so it is
  // called out rather than left for somebody to "fix".
  const overnight = item?.start && item?.end && item.end <= item.start ? ' · overnight' : '';
  return `${dayPart} · ${timePart}${datePart ? ` · ${datePart}` : ''}${overnight}`;
}

const emptyItem = () => ({
  id: '', name: '', price: '', notes: '', photo: '', category: '',
  days: {}, start: '', end: '', startDate: '', endDate: '',
});

// `panels` narrows the screen to some of the four, in PANELS order. /admin
// passes nothing and gets all four; Volunteer Tools splits them by tool —
// the review queue and handles are the Content Moderator's, Weekly Highlights
// and day-based items the Content Filler's — so each volunteer sees only the
// work their grant covers (the server enforces the same split per op).
export default function RestaurantsAdmin({ token, onExpired, panels: only = null, onCount = null }) {
  const shown = only ? PANELS.filter((p) => only.includes(p.id)) : PANELS;
  const [panel, setPanel] = useState(shown[0]?.id || 'queue');
  const [data, setData] = useState(null);
  const [places, setPlaces] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // One key at a time, the way AdminPage's `triageBusy` works: the id of the
  // row currently being written, so its own buttons disable and the rest of the
  // list stays usable.
  const [rowBusy, setRowBusy] = useState('');
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState({});          // review id → the note being typed
  const [picks, setPicks] = useState([]);          // the editable copy
  const [tempPlace, setTempPlace] = useState('');
  const [tempItems, setTempItems] = useState([]);  // the editable copy
  // The support form: which author key is being acted on, and the handle being
  // typed for them. Kept as one pair rather than a map, because changing a
  // handle is a conversation with one person at a time.
  const [handleAuthor, setHandleAuthor] = useState('');
  const [handleDraft, setHandleDraft] = useState('');
  const [handleMsg, setHandleMsg] = useState('');
  const [dish, setDish] = useState(null);          // /api/place-photo?part=admin, dish rows only
  const uploadFor = useRef(null);                  // which pick an upload is for
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    if (!token) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/restaurants?part=admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { onExpired?.(); setBusy(false); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || 'Failed to load.'); setBusy(false); return; }
      setData(j);
      setPicks((j.topPicks || []).map((p) => ({ ...p })));
    } catch { setErr('Network error.'); }
    setBusy(false);
  }, [token, onExpired]);

  useEffect(() => { load(); }, [load]);

  // Dish photos live in the place-photo store, not this one — a picture of a
  // dish is moderated like any student photo — so they are read from there.
  const wantsPhotos = shown.some((p) => p.id === 'photos');
  const loadDish = useCallback(async () => {
    if (!token || !wantsPhotos) return;
    try {
      const r = await fetch(`${API_BASE}/api/place-photo?part=admin`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) { onExpired?.(); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || 'Could not load dish photos.'); return; }
      const live = [];
      for (const [key, list] of Object.entries(j.items || {})) {
        const [placeId, itemId] = key.split('|');
        for (const a of Array.isArray(list) ? list : []) live.push({ ...a, key, placeId, itemId });
      }
      live.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setDish({ pending: (j.pending || []).filter((x) => x.itemId), live });
    } catch { setErr('Network error.'); }
  }, [token, onExpired, wantsPhotos]);
  useEffect(() => { loadDish(); }, [loadDish]);

  // What is still waiting — reviews, plus dish photos when that panel is shown —
  // for the host's tab badge, kept current after every decision and delete.
  useEffect(() => {
    if (!onCount || !data) return;
    onCount((data.counts?.pending || 0) + (dish?.pending.length || 0));
  }, [onCount, data, dish]);

  const photoOp = useCallback(async (body, key) => {
    setRowBusy(key); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/place-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401) { onExpired?.(); return; }
      if (!r.ok || j.ok === false) setErr(j.error === 'not found' ? 'That photo was already gone — refreshed.' : (j.error || 'That did not go through.'));
    } catch { setErr('Network error.'); }
    finally { setRowBusy(''); }
    await loadDish();
  }, [token, onExpired, loadDish]);

  // The names behind the ids. Read-only, and the ONLY reason this screen
  // touches places.json at all.
  useEffect(() => {
    let live = true;
    fetch(encodeURI('/Map Info/pathfinder/places.json'))
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!live) return;
        setPlaces((Array.isArray(rows) ? rows : [])
          .filter((p) => p && p.type === 'restaurant')
          .map((p) => ({
            id: p.id,
            name: p.name || 'Unnamed',
            items: (Array.isArray(p.menuItems) ? p.menuItems : [])
              .map((it) => ({ id: it.id, name: it.name || 'Unnamed item' })),
          }))
          .sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => { /* names are a convenience; the ids still moderate */ });
    return () => { live = false; };
  }, []);

  const byId = useMemo(() => {
    const m = new Map();
    for (const p of places) {
      m.set(p.id, { name: p.name, items: new Map(p.items.map((i) => [i.id, i.name])) });
    }
    return m;
  }, [places]);

  const placeName = useCallback((id) => byId.get(id)?.name || id || '—', [byId]);
  const itemName = useCallback((placeId, itemId) => byId.get(placeId)?.items.get(itemId) || itemId || '—', [byId]);

  /** Every op on this screen goes through here, so the 401 is handled once. */
  const post = useCallback(async (body, key) => {
    setRowBusy(key || 'all'); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/restaurants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 401) { onExpired?.(); return null; }
      // ⚠️ `error` on a handle op is a machine CODE ("taken", "locked") and
      // `reason` is the sentence written for a person — so `reason` wins where
      // there is one. Printing "taken" at a moderator is printing our wire
      // format at them.
      if (!r.ok || j.ok === false) { setErr(j.reason || j.error || 'That did not go through.'); return null; }
      return j;
    } catch { setErr('Network error.'); return null; }
    finally { setRowBusy(''); }
  }, [token, onExpired]);

  // Approving is what puts a review in front of students, so the queue is
  // reloaded afterwards rather than patched locally — one source of truth, the
  // rule AdminPage's photo queue follows.
  const decide = useCallback(async (row, next) => {
    const j = await post({ op: 'review-status', id: row.id, status: next, note: notes[row.id] ?? row.note ?? '' }, row.id);
    if (j) await load();
  }, [post, load, notes]);

  // Deleting is final (the review leaves every list and the average, and its
  // photos are deleted from storage), so it asks first.
  const removeReview = useCallback(async (row) => {
    const what = row.text ? `“${row.text.slice(0, 60)}${row.text.length > 60 ? '…' : ''}”` : 'this rating';
    if (!window.confirm(`Delete ${what} for good? This can't be undone.`)) return;
    const j = await post({ op: 'delete-review', id: row.id }, row.id);
    if (j) await load();
  }, [post, load]);

  const clearDenied = useCallback(async (n) => {
    if (!window.confirm(`Delete all ${n} denied review${n === 1 ? '' : 's'} for good? This can't be undone.`)) return;
    const j = await post({ op: 'clear-denied' }, 'clear-denied');
    if (j) await load();
  }, [post, load]);

  const savePicks = useCallback(async () => {
    const j = await post({ op: 'set-top-picks', picks }, 'picks');
    if (j) { setPicks((j.picks || []).map((p) => ({ ...p }))); setData((d) => (d ? { ...d, topPicks: j.picks } : d)); }
  }, [post, picks]);

  const saveTemp = useCallback(async () => {
    const j = await post({ op: 'set-content', placeId: tempPlace, tempItems }, 'temp');
    if (j) {
      setTempItems(j.tempItems || []);
      setData((d) => {
        if (!d) return d;
        const content = { ...(d.content || {}) };
        if (j.tempItems.length) content[j.placeId] = { tempItems: j.tempItems, updatedAt: new Date().toISOString() };
        else delete content[j.placeId];
        return { ...d, content };
      });
    }
  }, [post, tempPlace, tempItems]);

  // The `upload` op, reused from the public side: the same Blob directory, the
  // same MIME allowlist, the same 3.5 MB ceiling. A pasted URL still works —
  // this is the path for a photo that only exists on the operator's laptop.
  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const idx = uploadFor.current;
    if (!file || idx == null) return;
    const dataUrl = await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ''));
      fr.onerror = () => resolve('');
      fr.readAsDataURL(file);
    });
    if (!dataUrl) { setErr('That file could not be read.'); return; }
    const j = await post({ op: 'upload', filename: file.name, dataUrl }, `upload-${idx}`);
    if (j?.url) setPicks((list) => list.map((p, i) => (i === idx ? { ...p, image: j.url } : p)));
  }, [post]);

  // Both handle ops reload the whole screen afterwards rather than patching
  // state, the rule `decide` follows: a handle shows on every one of that
  // author's rows in all three queues, so a local patch would have to find them
  // all and would be wrong in whichever it missed.
  const saveHandle = useCallback(async () => {
    setHandleMsg('');
    const j = await post({ op: 'set-handle', author: handleAuthor.trim(), handle: handleDraft }, 'handle');
    if (j) {
      setHandleMsg(j.previous && j.previous !== j.handle
        ? `${j.handle} set — ${j.previous} is free again.`
        : `${j.handle} set.`);
      setHandleDraft(j.handle || '');
      await load();
    }
  }, [post, load, handleAuthor, handleDraft]);

  const freeHandle = useCallback(async (h) => {
    setHandleMsg('');
    const j = await post({ op: 'release-handle', handle: h }, `release-${h}`);
    if (j) {
      setHandleMsg(`${j.handle} released. That reviewer's past reviews now read as anonymous.`);
      if (j.author === handleAuthor) setHandleDraft('');
      await load();
    }
  }, [post, load, handleAuthor]);

  /** The registry as rows: who holds what, and how much they have written. */
  const handleRows = useMemo(() => {
    const reg = data?.handles || {};
    const counts = {};
    for (const list of ['pending', 'approved', 'denied']) {
      for (const r of data?.[list] || []) if (r.author) counts[r.author] = (counts[r.author] || 0) + 1;
    }
    return Object.entries(reg)
      .map(([author, handle]) => ({ author, handle, reviews: counts[author] || 0 }))
      .sort((a, b) => a.handle.localeCompare(b.handle));
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    // Pending FIRST and on its own by default, because it is the only one of
    // the three that is work. The others are the record.
    if (status === 'all') return [...(data.pending || []), ...(data.approved || []), ...(data.denied || [])];
    return data[status] || [];
  }, [data, status]);

  const counts = data?.counts || { pending: 0, approved: 0, denied: 0 };
  const tempPlaceRow = tempPlace ? byId.get(tempPlace) : null;
  const tempUpdatedAt = tempPlace ? data?.content?.[tempPlace]?.updatedAt : '';

  const setItem = (i, patch) => setTempItems((list) => list.map((t, n) => (n === i ? { ...t, ...patch } : t)));
  const setPick = (i, patch) => setPicks((list) => list.map((p, n) => (n === i ? { ...p, ...patch } : p)));
  const movePick = (i, by) => setPicks((list) => {
    const next = [...list];
    const j = i + by;
    if (j < 0 || j >= next.length) return list;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  return (
    <div>
      <input ref={fileInput} type="file" accept="image/*" style={S.hidden} onChange={onFile} />

      <div style={S.head}>
        <div style={S.panels}>
          {shown.map((p) => (
            <button
              key={p.id}
              style={{ ...S.panelBtn, ...(panel === p.id ? S.panelOn : {}) }}
              onClick={() => setPanel(p.id)}
            >
              {p.label}
              {p.id === 'queue' && counts.pending > 0 && <span style={S.dot} />}
              {p.id === 'photos' && dish?.pending.length > 0 && <span style={S.dot} />}
            </button>
          ))}
        </div>
        <button style={S.ghost} onClick={() => { load(); loadDish(); }} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
      </div>

      {/* Said once, at the top, because it is the rule the whole screen is
          built around and the one thing somebody adding a field here needs to
          know before they add it. */}
      <div style={S.note}>
        Everything on this screen is an <b>overlay</b> stored beside the menus, never a change to
        them. The authored menu comes from UMapsTools; editing it here would re-hash the campus
        data file and cost every phone on campus a 13&nbsp;MB download.
      </div>

      {err && <div style={S.err}>{err}</div>}

      {/* ── Review queue ───────────────────────────────────────────────── */}
      {panel === 'queue' && (
        <>
          <div style={S.chips}>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                style={{ ...S.chip, ...(status === f.id ? S.chipOn : {}) }}
                onClick={() => setStatus(f.id)}
              >
                {f.label} ({f.id === 'all'
                  ? counts.pending + counts.approved + counts.denied
                  : counts[f.id] || 0})
              </button>
            ))}
          </div>

          {status === 'denied' && counts.denied > 0 && (
            <div style={{ ...S.actions, marginTop: 0, marginBottom: 12 }}>
              <button style={S.deny} disabled={rowBusy === 'clear-denied'} onClick={() => clearDenied(counts.denied)}>
                {rowBusy === 'clear-denied' ? 'Deleting…' : `Delete all ${counts.denied} denied`}
              </button>
              <span style={S.lastEdited}>Removes them for good, photos included, to clear the list.</span>
            </div>
          )}

          {/* ⚠️ Star ratings skip this queue (api/_restaurantStore.js
              `addReview`), so a moderator who goes looking for one in Pending
              needs telling where it went and how to take one down. */}
          <div style={S.hint}>
            Star ratings with no words and no photos go live at once and never wait here. They are
            under <b>Approved</b>, one per phone per dish — a re-rate replaces the earlier one, and
            approving a written review retires the same phone&rsquo;s older rating of that dish.
            To take an abusive rating out of the average, <b>Deny</b> it there, or <b>Delete</b> it to remove it
            for good. Approved is exactly what the live app shows.
          </div>

          <div style={S.list}>
            {!rows.length && <div style={S.empty}>Nothing here.</div>}
            {rows.map((r) => {
              const draft = notes[r.id] ?? r.note ?? '';
              const working = rowBusy === r.id;
              return (
                <div key={r.id} style={S.card}>
                  <div style={S.cardHead}>
                    <span style={S.stars}>{stars(r.stars)}</span>
                    <span style={S.where}>
                      {placeName(r.placeId)} <span style={S.arrow}>·</span> {itemName(r.placeId, r.itemId)}
                    </span>
                    <span style={{ ...S.badge, ...(TONE[r.status] || {}) }}>{r.status}</span>
                    {!r.text && !r.photos?.length && <span style={S.claim}>rating only</span>}
                    {/* Stacked before re-rates replaced each other: this card
                        is the newest, and deciding it retires the rest. */}
                    {r.olderRatings > 0 && (
                      <span style={S.claim} title="Older ratings of this dish from the same phone. Deciding this one retires them.">
                        +{r.olderRatings} older
                      </span>
                    )}
                    <span style={S.when}>{fmtTime(r.createdAt)}</span>
                  </div>

                  {!!r.text && <div style={S.text}>{r.text}</div>}

                  {!!r.photos?.length && (
                    <div style={S.thumbs}>
                      {r.photos.map((p, i) => (
                        <a key={i} href={photoUrl(p)} target="_blank" rel="noopener noreferrer">
                          <img style={S.thumb} src={photoUrl(p)} alt="" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  )}

                  <div style={S.by}>
                    {/* ⚠️ `r.handle` is resolved from the registry at read
                        time, so it is what the app is printing RIGHT NOW —
                        `r.authorName` on a row stored before handles existed is
                        the old free-text name and is shown only when there is
                        no handle to show instead. */}
                    {r.handle
                      ? <span style={S.handle}>{r.handle}</span>
                      : (r.authorName || 'Anonymous')}
                    {/* ⚠️ A CLAIM, not a fact we checked — the field is named
                        supporterClaim for the same reason. */}
                    {r.supporterClaim && <span style={S.claim}>says they are a supporter</span>}
                    {!!r.author && (
                      <button
                        style={S.link}
                        title={`Author key ${r.author}`}
                        onClick={() => {
                          setPanel('handles');
                          setHandleAuthor(r.author);
                          setHandleDraft(r.handle || '');
                          setHandleMsg('');
                        }}
                      >{r.handle ? 'Change handle' : 'Give a handle'}</button>
                    )}
                  </div>

                  <div style={S.noteBlock}>
                    <div style={S.noteLabel}>Moderator note</div>
                    <textarea
                      style={S.noteBox}
                      rows={2}
                      value={draft}
                      onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                      placeholder="Why this decision? A denied review keeps this, so the call stays auditable."
                    />
                    <div style={S.actions}>
                      <button
                        style={{ ...S.approve, ...(working ? S.off : {}) }}
                        disabled={working || r.status === 'approved'}
                        onClick={() => decide(r, 'approved')}
                      >{working ? 'Saving…' : 'Approve'}</button>
                      <button
                        style={{ ...S.deny, ...(working ? S.off : {}) }}
                        disabled={working || r.status === 'denied'}
                        onClick={() => decide(r, 'denied')}
                      >Deny</button>
                      {r.status !== 'pending' && (
                        <button style={S.ghost} disabled={working} onClick={() => decide(r, 'pending')}>
                          Back to pending
                        </button>
                      )}
                      <button style={S.miniDanger} disabled={working} onClick={() => removeReview(r)}>Delete</button>
                      {r.updatedAt !== r.createdAt && (
                        <span style={S.lastEdited}>
                          Last edited {fmtTime(r.updatedAt)}{r.decidedBy ? ` by ${r.decidedBy}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Dish photos ────────────────────────────────────────────────── */}
      {panel === 'photos' && (
        <>
          <div style={S.hint}>
            Students' pictures of menu items. Accept the ones that clearly show the dish and nobody's face;
            an accepted photo becomes that dish's picture in the app. <b>Live</b> is what the app shows right now —
            delete anything that shouldn't be there.
          </div>
          {!dish ? <div style={S.empty}>Loading…</div> : (
            <>
              <div style={S.noteLabel}>Waiting ({dish.pending.length})</div>
              {!dish.pending.length ? <div style={{ ...S.empty, marginBottom: 16 }}>No dish photos waiting.</div> : (
                <div style={S.photoGrid}>
                  {dish.pending.map((p) => (
                    <div key={p.id} style={S.photoCard}>
                      <a href={photoUrl(p.url)} target="_blank" rel="noopener noreferrer">
                        <img style={S.photo} src={photoUrl(p.url)} alt={`Submitted by a student: ${p.itemName}`} loading="lazy" />
                      </a>
                      <div style={S.photoBody}>
                        <div style={S.where}>{byId.get(p.placeId)?.items.get(p.itemId) || p.itemName || p.itemId}</div>
                        <div style={S.lastEdited}>{byId.get(p.placeId)?.name || p.placeName || p.placeId}{p.ts ? ` · ${fmtTime(p.ts)}` : ''}</div>
                        <div style={S.actions}>
                          <button style={S.approve} disabled={rowBusy === p.id} onClick={() => photoOp({ op: 'accept', id: p.id }, p.id)}>Accept</button>
                          <button style={S.deny} disabled={rowBusy === p.id} onClick={() => photoOp({ op: 'reject', id: p.id }, p.id)}>Reject</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={S.noteLabel}>Live ({dish.live.length})</div>
              {!dish.live.length ? <div style={S.empty}>No dish photos are live.</div> : (
                <div style={S.photoGrid}>
                  {dish.live.map((p) => (
                    <div key={`${p.key}/${p.id}`} style={S.photoCard}>
                      <a href={photoUrl(p.url)} target="_blank" rel="noopener noreferrer">
                        <img style={S.photo} src={photoUrl(p.url)} alt={`Live in the app: ${p.itemName || p.itemId}`} loading="lazy" />
                      </a>
                      <div style={S.photoBody}>
                        <div style={S.where}>{byId.get(p.placeId)?.items.get(p.itemId) || p.itemName || p.itemId}</div>
                        <div style={S.lastEdited}>{byId.get(p.placeId)?.name || p.placeName || p.placeId}{p.ts ? ` · ${fmtTime(p.ts)}` : ''}</div>
                        <div style={S.actions}>
                          <button
                            style={S.miniDanger}
                            disabled={rowBusy === p.id}
                            onClick={() => {
                              if (window.confirm('Delete this photo from the live app?')) photoOp({ op: 'remove', placeId: p.key, id: p.id }, p.id);
                            }}
                          >{rowBusy === p.id ? 'Deleting…' : 'Delete'}</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Weekly Highlights ──────────────────────────────────────────── */}
      {panel === 'picks' && (
        <>
          <div style={S.hint}>
            Up to {MAX_PICKS}. The app draws a dot per pick, so three reads best — but two and four
            work. Order here is the order there. The photo fills the card and the title and
            subtitle are drawn over it on a blue banner across the corner, so keep them short and
            let the picture do the work. A pick with neither draws no banner at all.
          </div>
          <div style={S.list}>
            {picks.map((p, i) => (
              <div key={p.id || i} style={S.card}>
                <div style={S.cardHead}>
                  <span style={S.rank}>{i + 1}</span>
                  <span style={S.where}>{placeName(p.placeId)}</span>
                  <div style={S.spacer} />
                  <button style={S.mini} disabled={i === 0} onClick={() => movePick(i, -1)}>↑</button>
                  <button style={S.mini} disabled={i === picks.length - 1} onClick={() => movePick(i, 1)}>↓</button>
                  <button style={S.miniDanger} onClick={() => setPicks((l) => l.filter((_, n) => n !== i))}>Remove</button>
                </div>

                <div style={S.grid}>
                  <label style={S.label}>Restaurant
                    <select
                      style={S.input}
                      value={p.placeId || ''}
                      onChange={(e) => setPick(i, { placeId: e.target.value, itemId: '' })}
                    >
                      <option value="">Pick one…</option>
                      {places.map((pl) => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
                    </select>
                  </label>

                  <label style={S.label}>Menu item (optional)
                    <select style={S.input} value={p.itemId || ''} onChange={(e) => setPick(i, { itemId: e.target.value })}>
                      <option value="">The restaurant itself</option>
                      {(places.find((pl) => pl.id === p.placeId)?.items || [])
                        .map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                  </label>

                  <label style={S.label}>Image URL
                    <div style={S.row}>
                      <input
                        style={{ ...S.input, flex: 1 }}
                        value={p.image || ''}
                        onChange={(e) => setPick(i, { image: e.target.value })}
                        placeholder="Paste a URL, or upload"
                      />
                      <button
                        style={S.ghost}
                        disabled={rowBusy === `upload-${i}`}
                        onClick={() => { uploadFor.current = i; fileInput.current?.click(); }}
                      >{rowBusy === `upload-${i}` ? 'Uploading…' : 'Upload'}</button>
                    </div>
                  </label>

                  {/* The two lines the phone draws over the photo. Title is
                      WHAT (a dish, a counter, a deal), subtitle is the reason
                      to care (a price, a day, a condition) — they are set in
                      different type, which is why they are two fields rather
                      than one line the operator formats by hand.

                      `maxLength` matches the server's own caps rather than
                      being a nicer round number: the band is about three
                      quarters of a card wide, so anything past them truncates
                      on the phone, and a box that accepts what the reader will
                      never see quietly loses somebody's words. */}
                  <label style={S.label}>Title
                    <input
                      style={S.input}
                      value={p.title || ''}
                      maxLength={MAX_PICK_TITLE}
                      onChange={(e) => setPick(i, { title: e.target.value })}
                      placeholder="Mocha Drink"
                    />
                  </label>

                  <label style={S.label}>Subtitle
                    <input
                      style={S.input}
                      value={p.subtitle || ''}
                      maxLength={MAX_PICK_SUBTITLE}
                      onChange={(e) => setPick(i, { subtitle: e.target.value })}
                      placeholder="$3.99 — Wednesdays only"
                    />
                  </label>

                  {/* ⚠️ Kept, and kept editable, because app builds that
                      predate the banner render THIS and nothing else. Left
                      blank the server copies the title into it, so nobody has
                      to think about those readers — it is here for the operator
                      who wants to say something different to them, and so that
                      a caption already stored is visible rather than hidden
                      and unremovable. */}
                  <label style={S.label}>Caption (older app versions)
                    <input
                      style={S.input}
                      value={p.caption || ''}
                      onChange={(e) => setPick(i, { caption: e.target.value })}
                      placeholder="Leave blank to reuse the title"
                    />
                  </label>
                </div>

                {!!p.image && <img style={S.preview} src={photoUrl(p.image)} alt="" />}
              </div>
            ))}

            {picks.length < MAX_PICKS && (
              <button
                style={S.add}
                onClick={() => setPicks((l) => [
                  ...l,
                  { id: '', placeId: '', itemId: '', image: '', title: '', subtitle: '', caption: '', order: l.length },
                ])}
              >
                + Add a pick
              </button>
            )}
          </div>

          <div style={S.actions}>
            <button style={{ ...S.save, ...(rowBusy === 'picks' ? S.off : {}) }} disabled={rowBusy === 'picks'} onClick={savePicks}>
              {rowBusy === 'picks' ? 'Saving…' : 'Save highlights'}
            </button>
            {!!data?.topPicksUpdatedAt && <span style={S.lastEdited}>Last edited {fmtTime(data.topPicksUpdatedAt)}</span>}
          </div>
        </>
      )}

      {/* ── Temporary menu items ───────────────────────────────────────── */}
      {panel === 'temp' && (
        <>
          <div style={S.hint}>
            A special, a seasonal drink, a week of samosas. Everything about the schedule is
            optional and blank means <b>always</b>: no day ticked is every day, no window is all
            day, no dates is every week.
          </div>

          <label style={{ ...S.label, maxWidth: 380 }}>Restaurant
            <select
              style={S.input}
              value={tempPlace}
              onChange={(e) => {
                const id = e.target.value;
                setTempPlace(id);
                setTempItems((data?.content?.[id]?.tempItems || []).map((t) => ({ ...t })));
              }}
            >
              <option value="">Pick one…</option>
              {places.map((pl) => (
                <option key={pl.id} value={pl.id}>
                  {pl.name}{data?.content?.[pl.id]?.tempItems?.length ? ` (${data.content[pl.id].tempItems.length})` : ''}
                </option>
              ))}
            </select>
          </label>

          {!!tempPlace && (
            <>
              <div style={S.list}>
                {!tempItems.length && <div style={S.empty}>No temporary items at {tempPlaceRow?.name || 'this restaurant'} yet.</div>}
                {tempItems.map((t, i) => (
                  <div key={t.id || i} style={S.card}>
                    <div style={S.cardHead}>
                      <span style={S.where}>{t.name || 'New item'}</span>
                      <span style={S.schedule}>{scheduleLine(t)}</span>
                      <div style={S.spacer} />
                      <button style={S.miniDanger} onClick={() => setTempItems((l) => l.filter((_, n) => n !== i))}>Remove</button>
                    </div>

                    <div style={S.grid}>
                      <label style={S.label}>Name
                        <input style={S.input} value={t.name} onChange={(e) => setItem(i, { name: e.target.value })} />
                      </label>
                      <label style={S.label}>Price
                        {/* A STRING on purpose: blank is "no printed price", and
                            a number field would turn it into 0 — a free coffee. */}
                        <input style={S.input} value={t.price} onChange={(e) => setItem(i, { price: e.target.value })} placeholder="4.25" />
                      </label>
                      <label style={S.label}>Category
                        <input style={S.input} value={t.category} onChange={(e) => setItem(i, { category: e.target.value })} placeholder="Drinks" />
                      </label>
                      <label style={S.label}>Photo URL
                        <input style={S.input} value={t.photo} onChange={(e) => setItem(i, { photo: e.target.value })} />
                      </label>
                      <label style={{ ...S.label, gridColumn: '1 / -1' }}>Notes
                        <input style={S.input} value={t.notes} onChange={(e) => setItem(i, { notes: e.target.value })} placeholder="While it lasts." />
                      </label>
                    </div>

                    <div style={S.sched}>
                      <div style={S.noteLabel}>Days</div>
                      <div style={S.days}>
                        {DAY_KEYS.map((k) => (
                          <button
                            key={k}
                            style={{ ...S.day, ...(t.days?.[k] ? S.dayOn : {}) }}
                            onClick={() => {
                              const days = { ...(t.days || {}) };
                              if (days[k]) delete days[k]; else days[k] = true;
                              setItem(i, { days });
                            }}
                          >{DAY_SHORT[k]}</button>
                        ))}
                        {!DAY_KEYS.some((k) => t.days?.[k]) && <span style={S.dayNote}>none ticked = every day</span>}
                      </div>

                      <div style={S.grid}>
                        <label style={S.label}>From (time)
                          {/* Native time/date inputs here and not the three-part
                              control the club wizard uses: this is one operator
                              at a keyboard, not a student on a phone. */}
                          <input style={S.input} type="time" value={t.start} onChange={(e) => setItem(i, { start: e.target.value })} />
                        </label>
                        <label style={S.label}>To (time)
                          <input style={S.input} type="time" value={t.end} onChange={(e) => setItem(i, { end: e.target.value })} />
                        </label>
                        <label style={S.label}>First day
                          <input style={S.input} type="date" value={t.startDate} onChange={(e) => setItem(i, { startDate: e.target.value })} />
                        </label>
                        <label style={S.label}>Last day
                          <input style={S.input} type="date" value={t.endDate} onChange={(e) => setItem(i, { endDate: e.target.value })} />
                        </label>
                      </div>
                    </div>
                  </div>
                ))}

                <button style={S.add} onClick={() => setTempItems((l) => [...l, emptyItem()])}>+ Add an item</button>
              </div>

              <div style={S.actions}>
                <button style={{ ...S.save, ...(rowBusy === 'temp' ? S.off : {}) }} disabled={rowBusy === 'temp'} onClick={saveTemp}>
                  {rowBusy === 'temp' ? 'Saving…' : 'Save items'}
                </button>
                <span style={S.lastEdited}>
                  {tempUpdatedAt ? `Last edited ${fmtTime(tempUpdatedAt)}` : 'Never saved'}
                  {' · '}An item with no name is dropped on save.
                </span>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Reviewer handles ───────────────────────────────────────────── */}
      {panel === 'handles' && (
        <>
          <div style={S.hint}>
            A reviewer claims their <b>@handle</b> in the app once and is never offered a rename —
            this screen is the only way one changes. A handle is stored against the reviewer's
            author key and resolved when a review is read, so anything saved here shows on
            <b> every one of their past reviews</b> straight away.
          </div>

          <div style={S.card}>
            <div style={S.noteLabel}>Set or move a handle</div>
            <div style={S.grid}>
              <label style={S.label}>Author key
                {/* The opaque 12-hex key off a review row. ⚠️ There is nothing
                    on this server that turns it back into a phone, which is the
                    point — it identifies a reviewer's work and nothing else. */}
                <input
                  style={S.input}
                  value={handleAuthor}
                  onChange={(e) => { setHandleAuthor(e.target.value); setHandleMsg(''); }}
                  placeholder="4f9a1c2b7d30"
                />
              </label>
              <label style={S.label}>Handle
                <input
                  style={S.input}
                  value={handleDraft}
                  onChange={(e) => { setHandleDraft(e.target.value); setHandleMsg(''); }}
                  placeholder="@toast"
                />
              </label>
            </div>
            <div style={S.actions}>
              <button
                style={{ ...S.save, ...(rowBusy === 'handle' || !handleAuthor.trim() ? S.off : {}) }}
                disabled={rowBusy === 'handle' || !handleAuthor.trim()}
                onClick={saveHandle}
              >{rowBusy === 'handle' ? 'Saving…' : 'Set handle'}</button>
              <span style={S.lastEdited}>
                {handleMsg || 'Whatever they held before is freed for somebody else.'}
              </span>
            </div>
          </div>

          <div style={S.list}>
            {!handleRows.length && <div style={S.empty}>Nobody has claimed a handle yet.</div>}
            {handleRows.map((h) => (
              <div key={h.author} style={S.card}>
                <div style={S.cardHead}>
                  <span style={S.handle}>@{h.handle}</span>
                  <span style={S.key}>{h.author}</span>
                  <span style={S.lastEdited}>
                    {h.reviews === 1 ? '1 review' : `${h.reviews} reviews`}
                  </span>
                  <div style={S.spacer} />
                  <button
                    style={S.mini2}
                    onClick={() => { setHandleAuthor(h.author); setHandleDraft(`@${h.handle}`); setHandleMsg(''); }}
                  >Change</button>
                  <button
                    style={{ ...S.miniDanger, ...(rowBusy === `release-${h.handle}` ? S.off : {}) }}
                    disabled={rowBusy === `release-${h.handle}`}
                    onClick={() => freeHandle(h.handle)}
                  >{rowBusy === `release-${h.handle}` ? 'Releasing…' : 'Release'}</button>
                </div>
              </div>
            ))}
          </div>

          {/* Said here rather than in a confirm dialog: releasing is the right
              answer to impersonation and should not feel dangerous, but what it
              does to the person's back catalogue is not obvious. */}
          <div style={S.hint}>
            <b>Release</b> frees a handle for anybody to claim — for impersonation, or a name that
            turns out to be somebody's real one. The reviewer keeps their reviews; those reviews
            simply stop carrying a name until they claim another.
          </div>
        </>
      )}
    </div>
  );
}

// Amber is waiting on somebody, green is published, and a denial is plain
// rather than an alarm: refusing a review is an ordinary outcome.
const TONE = {
  pending: { background: '#fef7e0', color: '#b06000' },
  approved: { background: '#e6f4ea', color: '#137333' },
  denied: { background: '#f1f3f4', color: '#3c4043' },
};

const S = {
  hidden: { display: 'none' },
  head: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  panels: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  panelBtn: { background: '#fff', border: '1.5px solid #dadce0', borderRadius: 999, padding: '8px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#202124' },
  panelOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  dot: { display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#d93025', marginLeft: 7, verticalAlign: 'middle' },
  ghost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  note: { background: '#e8f0fe', color: '#174ea6', border: '1px solid #d2e3fc', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 12 },
  hint: { fontSize: 12.5, color: '#5f6368', lineHeight: 1.45, margin: '0 0 12px' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  chip: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 600, color: '#3c4043', cursor: 'pointer', whiteSpace: 'nowrap' },
  chipOn: { background: '#1a73e8', border: '1px solid #1a73e8', color: '#fff' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
  empty: { background: '#fff', borderRadius: 12, padding: 16, fontSize: 14, color: '#5f6368' },
  card: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '12px 14px' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, marginBottom: 8 },
  stars: { color: '#f29900', fontSize: 15, letterSpacing: 1, flexShrink: 0 },
  where: { fontWeight: 700, color: '#202124' },
  arrow: { color: '#9aa0a6' },
  schedule: { fontSize: 12, color: '#5f6368', background: '#f1f3f4', borderRadius: 999, padding: '3px 10px' },
  badge: { borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 },
  when: { color: '#80868b', fontSize: 12, marginLeft: 'auto' },
  rank: { width: 22, height: 22, borderRadius: '50%', background: '#1a73e8', color: '#fff', fontWeight: 800, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  spacer: { flex: 1 },
  text: { fontSize: 14, color: '#202124', whiteSpace: 'pre-wrap', lineHeight: 1.4, marginBottom: 8 },
  thumbs: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  thumb: { width: 92, height: 92, objectFit: 'cover', borderRadius: 8, background: '#f1f3f4', display: 'block' },
  preview: { maxWidth: 220, maxHeight: 140, objectFit: 'cover', borderRadius: 8, marginTop: 8, display: 'block' },
  by: { fontSize: 12.5, color: '#5f6368', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  claim: { background: '#f1f3f4', borderRadius: 999, padding: '2px 8px', fontSize: 11.5, color: '#5f6368' },
  // A handle is an identity, not a label, so it gets the page's own accent
  // rather than the grey every other caption here is drawn in.
  handle: { color: '#1a73e8', fontWeight: 700, fontSize: 13 },
  key: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#80868b' },
  link: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, fontWeight: 700, color: '#1a73e8', cursor: 'pointer', textDecoration: 'underline' },
  noteBlock: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f3f4' },
  noteLabel: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', marginBottom: 6 },
  noteBox: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 13, lineHeight: 1.45, resize: 'vertical' },
  actions: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  approve: { background: '#188038', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  deny: { background: '#fff', color: '#c5221f', border: '1.5px solid #f3c4c1', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  save: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  off: { opacity: 0.6, cursor: 'default' },
  lastEdited: { fontSize: 12, color: '#80868b' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#5f6368' },
  input: { border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 13.5, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#202124', background: '#fff', boxSizing: 'border-box', minWidth: 0 },
  row: { display: 'flex', gap: 8, alignItems: 'center' },
  mini: { background: '#fff', border: '1px solid #dadce0', borderRadius: 8, width: 28, height: 28, fontSize: 13, fontWeight: 800, cursor: 'pointer', color: '#202124' },
  mini2: { background: '#fff', border: '1px solid #dadce0', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: '#202124' },
  miniDanger: { background: '#fff', color: '#c5221f', border: '1px solid #f3c4c1', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  add: { background: '#fff', border: '1.5px dashed #dadce0', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, fontWeight: 700, color: '#1a73e8', cursor: 'pointer' },
  sched: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f3f4', display: 'flex', flexDirection: 'column', gap: 10 },
  days: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  day: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '5px 11px', fontSize: 12.5, fontWeight: 700, color: '#3c4043', cursor: 'pointer' },
  dayOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  dayNote: { fontSize: 12, color: '#80868b' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 16 },
  photoCard: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  photo: { width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block', background: '#f1f3f4' },
  photoBody: { padding: '10px 12px' },
};
